import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';

/**
 * Enterprise-grade hook for Dashboard translations with type safety and error handling
 * Provides centralized access to all Dashboard-related translations with performance optimization
 */
export function useDashboardTranslations() {
  // Use the default translation namespace since dashboardpage.json is merged into translation
  const { t } = useTranslation('translation');

  // Memoize translation functions to prevent re-renders
  const translations = useMemo(() => {
    // Helper functions for common translation patterns with type safety
    const getHeaderText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.header.${key}`, params || {}));
    };

    const getToastText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.toast.${key}`, params || {}));
    };

    const getCardText = (
      cardName: string,
      key: string,
      params?: Record<string, unknown>
    ): string => {
      return String(t(`dashboardpage.cards.${cardName}.${key}`, params || {}));
    };

    const getTotalProductsText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.totalProducts.${key}`, params || {}));
    };

    const getDataCompletenessText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.dataCompleteness.${key}`, params || {}));
    };

    const getProductStatusText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.productStatus.${key}`, params || {}));
    };

    const getMostMissingFieldsText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.mostMissingFields.${key}`, params || {}));
    };

    const getIncompleteProductsText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.incompleteProducts.${key}`, params || {}));
    };

    const getRecentActivityText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.recentActivity.${key}`, params || {}));
    };

    const getActivityActionText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.recentActivity.actions.${key}`, params || {}));
    };

    const getActivityMessageText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.recentActivity.messages.${key}`, params || {}));
    };

    const getFieldLabelText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`fieldLabels.${key}`, params || {}));
    };

    const getEntityLabelText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`entityLabels.${key}`, params || {}));
    };

    const getCompletenessTrendText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.cards.completenessTrend.${key}`, params || {}));
    };

    const getTimeAgoText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.timeAgo.${key}`, params || {}));
    };

    const getFormatText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.format.${key}`, params || {}));
    };

    const getAriaText = (key: string, params?: Record<string, unknown>): string => {
      return String(t(`dashboardpage.aria.${key}`, params || {}));
    };

    // Safe translation wrapper that always returns string
    const tSafe = (key: string, params?: Record<string, unknown>): string => {
      return String(t(key, params || {}));
    };

    // Helper for time formatting with proper i18n
    const getTimeAgoFormatted = (date: string): string => {
      const now = new Date();
      const pastDate = new Date(date);
      const diff = now.getTime() - pastDate.getTime();

      const minutes = Math.floor(diff / 1000 / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 7) return pastDate.toLocaleDateString();
      if (days > 0) return getTimeAgoText('daysAgo', { count: days });
      if (hours > 0) return getTimeAgoText('hoursAgo', { count: hours });
      if (minutes > 0) return getTimeAgoText('minutesAgo', { count: minutes });
      return getTimeAgoText('justNow');
    };

    // Helper for user initials with i18n
    const getUserInitials = (userName: string): string => {
      if (!userName || userName === getRecentActivityText('systemUser')) {
        return getRecentActivityText('userInitials.system');
      }
      const names = userName.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return (
        userName.substring(0, 2).toUpperCase() || getRecentActivityText('userInitials.fallback')
      );
    };

    // Helper for severity level translation
    const getSeverityText = (level: string): string => {
      return getMostMissingFieldsText(`severity.${level}`);
    };

    // Helper for completeness trend stats
    const getStatsText = (statKey: string): string => {
      return getCompletenessTrendText(`stats.${statKey}`);
    };

    return {
      // Direct translation function with type safety
      t: tSafe,

      // Category-specific helpers
      getHeaderText,
      getToastText,
      getCardText,
      getTotalProductsText,
      getDataCompletenessText,
      getProductStatusText,
      getMostMissingFieldsText,
      getIncompleteProductsText,
      getRecentActivityText,
      getCompletenessTrendText,
      getTimeAgoText,
      getFormatText,
      getAriaText,

      // Specialized helpers
      getTimeAgoFormatted,
      getUserInitials,
      getSeverityText,
      getStatsText,

      // Activity-specific helpers
      getActivityActionText,
      getActivityMessageText,
      getFieldLabelText,
      getEntityLabelText,
    };
  }, [t]);

  return translations;
}

export type DashboardTranslations = ReturnType<typeof useDashboardTranslations>;
